/**
 * Cómo volver a encontrar un elemento: un selector CSS lo más corto y estable posible (id propio,
 * atributos de pruebas, o la ruta de etiquetas desde el ancestro con id más cercano) y un XPath de
 * respaldo. Dentro de un Web Component, el selector es relativo a su shadow root.
 */

/** Atributos que suelen poner los equipos para pruebas: estables entre versiones. */
const STABLE_ATTRS = ["data-testid", "data-test", "data-cy", "data-qa", "data-anota-id"];

/** Ids que parecen generados (React useId, hashes, contadores): cambian entre cargas. */
const GENERATED_ID = /^(?::?r[0-9a-z]*:?|[a-f0-9]{8,}|ember\d+|mui-\d+|radix-.+|headlessui-.+|.*\d{4,}.*)$/i;

const MIN_DEPTH = 3;

const esc = (value: string) => (typeof CSS !== "undefined" && CSS.escape ? CSS.escape(value) : value.replace(/[^\w-]/g, "\\$&"));

type Root = Document | ShadowRoot;

function isUnique(selector: string, root: Root): boolean {
  try {
    return root.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}

const stableId = (el: Element) => (el.id && !GENERATED_ID.test(el.id) ? `#${esc(el.id)}` : null);

export function cssSelector(el: Element): string {
  const root = el.getRootNode() as Root;
  const own = stableId(el);
  if (own && isUnique(own, root)) return own;
  for (const name of STABLE_ATTRS) {
    const value = el.getAttribute(name);
    const selector = value ? `[${name}="${esc(value)}"]` : null;
    if (selector && isUnique(selector, root)) return selector;
  }

  // Ruta de etiquetas hacia arriba: se ancla al ancestro con id estable más cercano. Sin ninguno, se
  // corta cuando ya es única con al menos MIN_DEPTH niveles (una ruta muy corta, como
  // "li:nth-of-type(2)", deja de ser única en cuanto la página crece).
  const parts: string[] = [];
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (node !== el) {
      const id = stableId(node);
      if (id && isUnique(id, root)) {
        parts.unshift(id);
        return parts.join(" > ");
      }
    }
    const tag = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;
    const current: Element = node;
    const same = parent ? Array.from(parent.children).filter((c) => c.tagName === current.tagName) : [];
    parts.unshift(same.length > 1 ? `${tag}:nth-of-type(${same.indexOf(current) + 1})` : tag);
    const candidate = parts.join(" > ");
    if (parts.length >= MIN_DEPTH && isUnique(candidate, root)) return candidate;
  }
  return parts.join(" > ");
}

export function xPath(el: Element): string {
  const parts: string[] = [];
  for (let node: Element | null = el; node; node = node.parentElement) {
    const current: Element = node;
    const same = current.parentElement ? Array.from(current.parentElement.children).filter((c) => c.tagName === current.tagName) : [current];
    const tag = current.tagName.toLowerCase();
    parts.unshift(same.length > 1 ? `${tag}[${same.indexOf(current) + 1}]` : tag);
  }
  return `/${parts.join("/")}`;
}
